import { ApiResponse } from '../../types';

const API_BASE = import.meta.env.VITE_ADMIN_URL;

/**
 * Obtiene los eventos de agenda por sucursal
 */
export const getEventosPorSucursal = async (sucursal: string, token: string, periodoId?: string): Promise<any[]> => {
  try {
    const url = new URL(`${API_BASE}/agenda/por-sucursal`);
    url.searchParams.append('sucursal', sucursal);
    
    // Si se proporciona periodoId, lo agregamos a los parámetros de consulta
    if (periodoId) {
      url.searchParams.append('periodoId', periodoId);
    }
    
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers
    });
    
    const res = await response.json();
    
    if (res && res.success && res.eventos) {
      return res.eventos;
    }
    
    return [];
  } catch (error) {
    console.error("Error al obtener eventos por sucursal:", error);
    return [];
  }
};

/**
 * Obtiene un evento específico por ID
 */
export const getEventoPorId = async (sucursal: string, token: string, periodoId: string, eventoId: string): Promise<any> => {
  try {
    const url = new URL(`${API_BASE}/agenda/por-usuario`);
    url.searchParams.append('sucursal', sucursal);
    url.searchParams.append('periodoId', periodoId);
    url.searchParams.append('eventoId', eventoId);
    
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers
    });
    
    const res = await response.json();
    
    if (res && res.success && res.eventos && res.eventos.length > 0) {
      return res.eventos[0];
    }
    
    return null;
  } catch (error) {
    console.error(`Error al obtener evento ${eventoId}:`, error);
    return null;
  }
};

/**
 * Obtiene los eventos de agenda por usuario
 * @param userId ID del usuario
 * @param sucursal ID de la sucursal  
 * @param token Token de autenticación
 * @param periodoId Período en formato YYYY-MM (OBLIGATORIO)
 * @param eventoId ID específico del evento (opcional)
 */
export const getEventosPorUsuario = async (
  userId: string, 
  sucursal: string, 
  token: string, 
  periodoId: string, 
  eventoId?: string
): Promise<any[]> => {
  try {
    if (!userId || !sucursal || !periodoId) {
      console.error('❌ Parámetros obligatorios faltantes:', { userId, sucursal, periodoId });
      return [];
    }
    
    const url = new URL(`${API_BASE}/agenda/por-usuario`);
    url.searchParams.append('userId', userId);
    url.searchParams.append('sucursal', sucursal);
    url.searchParams.append('periodoId', periodoId); // Ahora es obligatorio
    
    // Si se proporciona eventoId, lo agregamos para buscar un evento específico
    if (eventoId) {
      url.searchParams.append('eventoId', eventoId);
    }
    
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      console.error(`❌ Error HTTP ${response.status}: ${response.statusText}`);
      const errorText = await response.text();
      console.error(`💬 Respuesta de error:`, errorText);
      return [];
    }
    
    const res = await response.json();
    
    if (res && res.success && res.info && res.info.eventos) {
      return res.info.eventos;
    }
    
    if (res && res.success === false) {
      console.error(`⚠️ La API retornó success: false para periodo ${periodoId}. Mensaje:`, res.error || res.message);
    }
    
    return [];
  } catch (error) {
    console.error(`💥 Error al obtener eventos por usuario para periodo ${periodoId}:`, error);
    return [];
  }
};

/**
 * Crea un nuevo evento en la agenda
 */
export const crearEvento = async (
  token: string,
  datos: {
    titulo: string;
    descripcion?: string;
    fecha: string;
    horaInicio?: string;
    horaFin?: string;
    ubicacion?: string;
    participantes: string[];
    creadorId: string;
    color?: string;
    sucursal: string;
    periodoId?: string; // Parámetro opcional para identificar el período
  }
): Promise<ApiResponse> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Calcular el periodoId si no se proporciona
    if (!datos.periodoId) {
      // Formato de periodoId: YYYY-MM
      const fecha = new Date(datos.fecha);
      const ano = fecha.getFullYear();
      const mes = fecha.getMonth() + 1; // Los meses en JavaScript van de 0 a 11
      datos.periodoId = `${ano}-${mes < 10 ? '0' + mes : mes}`;
    }
    
    const response = await fetch(`${API_BASE}/agenda/crear`, {
      method: 'POST',
      headers,
      body: JSON.stringify(datos)
    });
    
    return await response.json();
  } catch (error) {
    console.error("Error al crear evento:", error);
    return {
      success: false,
      message: "Error al crear evento. Intente nuevamente."
    };
  }
};

/**
 * Actualiza un evento existente
 */
export const actualizarEvento = async (
  token: string,
  datos: {
    eventoId: string;
    titulo?: string;
    descripcion?: string;
    fecha?: string;
    horaInicio?: string;
    horaFin?: string;
    ubicacion?: string;
    participantes?: string[];
    color?: string;
    sucursal: string;
    periodoId?: string; // Parámetro opcional para identificar el período
    periodoIdAnterior?: string; // Para cuando cambia de período (por ejemplo, al cambiar de mes)
  }
): Promise<ApiResponse> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Calcular el periodoId si hay una fecha pero no se proporciona periodoId
    if (datos.fecha && !datos.periodoId) {
      const fecha = new Date(datos.fecha);
      const ano = fecha.getFullYear();
      const mes = fecha.getMonth() + 1; // Los meses en JavaScript van de 0 a 11
      datos.periodoId = `${ano}-${mes < 10 ? '0' + mes : mes}`;
    }
    
    const response = await fetch(`${API_BASE}/agenda/actualizar`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(datos)
    });
    
    return await response.json();
  } catch (error) {
    console.error("Error al actualizar evento:", error);
    return {
      success: false,
      message: "Error al actualizar evento. Intente nuevamente."
    };
  }
};

/**
 * Elimina un evento
 */
export const eliminarEvento = async (
  token: string,
  eventoId: string,
  sucursal: string,
  periodoId?: string
): Promise<ApiResponse> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE}/agenda/eliminar`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ eventoId, sucursal, periodoId })
    });
    
    return await response.json();
  } catch (error) {
    console.error("Error al eliminar evento:", error);
    return {
      success: false,
      message: "Error al eliminar evento. Intente nuevamente."
    };
  }
};

/**
 * Reagenda un evento
 */
export const reagendarEvento = async (
  token: string,
  datos: {
    eventoId: string;
    nuevaFecha: string;
    nuevaHoraInicio?: string;
    nuevaHoraFin?: string;
    sucursal: string;
    periodoId?: string; // El período actual del evento
    nuevoPeriodoId?: string; // El nuevo período si la fecha cambia de mes
  }
): Promise<ApiResponse> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Calcular el nuevo periodoId si hay una nueva fecha pero no se proporciona nuevoPeriodoId
    if (datos.nuevaFecha && !datos.nuevoPeriodoId) {
      const fecha = new Date(datos.nuevaFecha);
      const ano = fecha.getFullYear();
      const mes = fecha.getMonth() + 1; // Los meses en JavaScript van de 0 a 11
      datos.nuevoPeriodoId = `${ano}-${mes < 10 ? '0' + mes : mes}`;
    }
    
    const response = await fetch(`${API_BASE}/agenda/reagendar`, {
      method: 'POST',
      headers,
      body: JSON.stringify(datos)
    });
    
    return await response.json();
  } catch (error) {
    console.error("Error al reagendar evento:", error);
    return {
      success: false,
      message: "Error al reagendar evento. Intente nuevamente."
    };
  }
};

/**
 * Agrega una minuta a un evento
 */
export const agregarMinuta = async (
  token: string,
  datos: {
    eventoId: string;
    resumen: string;
    archivos?: any[];
    sucursal: string;
    periodoId?: string; // Parámetro opcional para identificar el período
  }
): Promise<ApiResponse> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE}/agenda/minuta`, {
      method: 'POST',
      headers,
      body: JSON.stringify(datos)
    });
    
    return await response.json();
  } catch (error) {
    console.error("Error al agregar minuta:", error);
    return {
      success: false,
      message: "Error al agregar minuta. Intente nuevamente."
    };
  }
};
